import { DecimalPipe, NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AvatarFullscreenHeaderSlot, ElevenLabsModelTypeArr, LiveAvatarConfig } from '@shared/index';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SliderModule } from 'primeng/slider';
import { FormForBooleanComponent } from 'src/app/shared/components/ai-bot/utils/form-for-boolean/form-for-boolean.component';
import { AudioBtnComponent } from 'src/app/shared/components/ai-bot/utils/bot-chat-dialog/audio-btn/audio-btn.component';
import { SubmenuComponent } from 'src/app/shared/components/submenu/submenu.component';
import { SubmenuserviceService } from 'src/app/shared/components/submenu/submenuservice.service';
import { AvatarService, LiveAvatarEntry } from '@shared-angular/services/avatar.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { VoiceService } from 'src/app/shared/services/voice.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { HintComponent } from '@shared-angular/components/hint/hint.component';
import { CodemirrorCssComponent } from 'src/app/shared/components/ai-bot/utils/codemirror-css/codemirror-css.component';

@Component({
  imports: [NgClass, NgStyle, DecimalPipe,
    FormsModule,
    FormForBooleanComponent,
    AudioBtnComponent,
    HintComponent,
    CodemirrorCssComponent,
    SubmenuComponent,
    CardModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    SliderModule
  ],
  templateUrl: './avatar-tab.component.html',
  styleUrl: './avatar-tab.component.scss'
})
export class AvatarTabComponent implements OnInit, AfterViewInit {
  protected elevenLabsModels = ElevenLabsModelTypeArr;
  protected elevenLabsModelOptions = [...ElevenLabsModelTypeArr];
  public avatars: LiveAvatarEntry[] = [];
  public avatarAspectRatios = new Map<string, number>();
  private readonly CARD_SIZE = 200;

  public readonly headerSlotKindOptions: { label: string, value: AvatarFullscreenHeaderSlot['kind'] }[] = [
    { label: $localize`:@@fullscreen-header-slot-none:Kein Inhalt`, value: 'none' },
    { label: $localize`:@@fullscreen-header-slot-image:Bild`, value: 'image' },
    { label: $localize`:@@fullscreen-header-slot-text:Text`, value: 'text' }
  ];

  // i18n
  public showPrompt = $localize`:@@show-prompt:Chat-Eingabezeile anzeigen`;
  public voiceInput = $localize`:@@voice-input:Spracheingabe`;
  public redirectChat = $localize`:@@redirect-chat:Wechseln zu Chat`;
  public redirectVoice = $localize`:@@redirect-voice:Wechseln zu Audio`;
  public cancelThinking = $localize`:@@cancel-thinking:Bot-Denken abbrechen`;
  public greenscreen = $localize`:@@greenscreen-mode:Greenscreen-Modus`;
  public enterCustomCss = $localize`:@@enter-custom-css:Gib deinen CSS-Code hier ein…`;
  public fullscreenMode = $localize`:@@fullscreen-mode:Fullscreen-Modus`;

  constructor(
    private avatarService: AvatarService,
    public rss: RouteShareService,
    public submenuService: SubmenuserviceService,
    protected voiceService: VoiceService,
    private toastr: ToastrService
  ) { }

  public async ngOnInit(): Promise<void> {
    this.avatars = await this.avatarService.getLiveAvatars(this.rss.botConfig.currentOrg);
    this.avatars.forEach(avatar => {
      if (!avatar.previewUrl) return;
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          this.avatarAspectRatios.set(avatar.id, img.naturalWidth / img.naturalHeight);
        }
      };
      img.src = avatar.previewUrl;
    });
    this.ensureLiveAvatarConfig();
    if (this.voiceService.voices.length === 0) {
      await this.voiceService.getVoices();
    }
    if (this.voiceService.voices.length !== 0 && this.config.voiceConfig.id && !this.voiceService.voices.find(v => v.voiceId === this.config.voiceConfig.id)) {
      this.config.voiceConfig.id = '';
      void this.rss.saveBotConfigField('liveAvatarConfig');
      this.toastr.info('BOT_UPDATED', 'Die bisherige Stimme wurde seitens Anbieter eingestellt. Bitte wähle eine neue Stimme.', { timeOut: 5000 });
    }
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      const id = this.rss.botConfig.liveAvatarConfig?.avatarId;
      if (!id) return;
      const index = this.avatars.findIndex(a => a.id === id) + 1;
      if (index > 0) {
        document.querySelector(`.avatar-grid .avatar-card:nth-child(${String(index)})`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'center' });
      }
    }, 500);
  }

  private ensureLiveAvatarConfig(): void {
    if (this.rss.botConfig.liveAvatarConfig) {
      // Bots, deren liveAvatarConfig vor Einführung des Fullscreen-Modus angelegt wurde, besitzen das Feld noch nicht.
      this.rss.botConfig.liveAvatarConfig.fullscreenConfig ??= this.defaultFullscreenConfig();
      return;
    }
    this.rss.botConfig.liveAvatarConfig = {
      avatarId: '',
      avatarName: '',
      avatarPreviewUrl: '',
      backgroundUrl: '',
      useGreenscreen: false,
      /* eslint-disable @typescript-eslint/no-unnecessary-condition */
      voiceConfig: this.rss.botConfig.voiceConfig ?? {
        id: '',
        stability: 0.5,
        use_speaker_boost: true,
        similarity_boost: 0.75,
        style: 0,
        speed: 1,
        model: 'eleven_flash_v2_5'
      },
      promptbarActive: this.rss.botConfig.promptbarAvatarActive ?? true,
      voiceInputActive: this.rss.botConfig.voiceInputAvatarActive ?? true,
      switchToChat: this.rss.botConfig.switchToChatFromAvatar ?? false,
      switchToVoice: this.rss.botConfig.switchToVoiceFromAvatar ?? false,
      cancelBotThinkingActive: this.rss.botConfig.cancelBotThinkingChatActive ?? false,
      /* eslint-enable @typescript-eslint/no-unnecessary-condition */
      fullscreenConfig: this.defaultFullscreenConfig()
    };
  }

  private defaultFullscreenConfig() {
    return {
      active: false,
      header: {
        left: { kind: 'none' as const, value: '' },
        center: { kind: 'none' as const, value: '' },
        right: { kind: 'none' as const, value: '' }
      }
    };
  }

  public get config(): LiveAvatarConfig {
    // ensureLiveAvatarConfig() is always called before this getter
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.rss.botConfig.liveAvatarConfig!;
  }

  public selectAvatar(avatar: LiveAvatarEntry): void {
    this.config.avatarId = avatar.id;
    this.config.avatarName = avatar.name;
    this.config.avatarPreviewUrl = avatar.previewUrl;
    void this.rss.saveBotConfigField('liveAvatarConfig');
  }

  public getInnerStyle(avatarId: string): Record<string, string> {
    const ratio = this.avatarAspectRatios.get(avatarId) ?? 1;
    let width: number;
    let height: number;
    if (ratio >= 1) {
      width = this.CARD_SIZE;
      height = Math.round(this.CARD_SIZE / ratio);
    } else {
      height = this.CARD_SIZE;
      width = Math.round(this.CARD_SIZE * ratio);
    }
    return { width: `${String(width)}px`, height: `${String(height)}px` };
  }

  public saveConfig(): void {
    void this.rss.saveBotConfigField('liveAvatarConfig');
  }

  // Im Fullscreen-Modus ist Greenscreen zwingend erforderlich (siehe Bot-seitiges effectiveGreenscreen) -
  // useGreenscreen wird beim Aktivieren daher konsistent mitgesetzt, auch wenn der Toggle selbst disabled ist.
  public toggleFullscreen(): void {
    if (this.config.fullscreenConfig?.active) {
      this.config.useGreenscreen = true;
    }
    this.saveConfig();
  }

  protected sliderChange(value: string, field: 'stability' | 'similarity_boost' | 'style' | 'speed'): void {
    this.config.voiceConfig[field] = parseFloat(value);
    void this.rss.saveBotConfigField('liveAvatarConfig');
  }
}
